import {FunctionComponent, useMemo} from 'react';
import clsx from 'clsx';
import {useTranslation} from 'react-i18next';
import {useLocation} from 'react-router';
import {twMerge} from 'tailwind-merge';
import CheckIcon from '@/assets/images/icon-check.svg?react';
import ExclamationPointIcon from '@/assets/images/icon-exclamation-point.svg?react';
import PencilIcon from '@/assets/images/icon-pencil.svg?react';
import {useDocumentContext} from '@/components/document/context';
import {TrackedButton} from '@/components/tracked-atoms/tracked-button';
import {DocumentSectionExcerpt} from '@/engine/types/document-model';
import environment from '@/environment';
import {TrackingClickPrefix} from '@/services/analytics/analytics-types';
import {getAnalyticsDocumentSectionVirtualClickUrl} from '@/services/analytics/get-analytics-document-section-virtual-click-url';
import {WEB_CLICK_TRACKING_KEY} from '@/services/analytics/web-click-tracking-key';

interface ElementProps {
  index: number;
  section: DocumentSectionExcerpt;
  activeSectionIndex: number;
  currentSectionIndex: number;
  onClick: () => void;
}

const SectionsSelectorMenuItem: FunctionComponent<ElementProps> = ({
  index,
  section,
  activeSectionIndex,
  currentSectionIndex,
  onClick,
}) => {
  const {t} = useTranslation();
  const location = useLocation();
  const {valueResolver} = useDocumentContext();

  const mergedClasses = clsx(
    'flex cursor-pointer flex-row justify-between py-3 text-sm font-medium leading-tight hover:text-violet',
    activeSectionIndex === index && 'text-violet hover:text-purple-925',
    activeSectionIndex !== index &&
      currentSectionIndex !== index &&
      section.isCompleted &&
      section.isRequired &&
      'text-grey-650 hover:text-violet',
    activeSectionIndex !== index && currentSectionIndex === index && 'hover:text-violet',
    !section.isRequired && 'pointer-events-none cursor-not-allowed opacity-30',
  );

  const sectionTitle = useMemo(() => {
    return [`${index + 1}.`, environment.DEBUG ? `${section.id} –` : '', valueResolver(section.title)].join(' ');
  }, [valueResolver, section, index]);

  return (
    <TrackedButton
      id={section.id}
      className={twMerge(mergedClasses)}
      onClick={onClick}
      disabled={!section.isRequired}
      data-testid={`link-section-${section.id}`}
      aria-label={t('components.sections-selector.menu.select-section.accessibility-button', {sectionName: section.id})}
      trackingKey={WEB_CLICK_TRACKING_KEY.SELECT_SECTION}
      trackingAs={TrackingClickPrefix.NAV}
      trackingClickUrl={getAnalyticsDocumentSectionVirtualClickUrl(location, section)}
    >
      <div className="flex flex-row items-start gap-2 text-left">
        {section.isCompleted && section.isRequired && (
          <CheckIcon className="size-4 shrink-0 rounded-full bg-green-950 fill-white p-0.5" />
        )}
        {!section.isCompleted && section.isRequired && (
          <ExclamationPointIcon className="size-4 shrink-0 rounded-full bg-grey-675 fill-white" />
        )}
        {!section.isRequired && <span className="size-4 shrink-0" />}
        {sectionTitle}
      </div>

      {section.isRequired && <PencilIcon className="h-4 shrink-0 stroke-violet" />}
    </TrackedButton>
  );
};

export default SectionsSelectorMenuItem;
